import { LOGIN_CUSTOMER } from '@/common/define/common.define';
import { webStorageController } from '@/common/utils/browser';
import { LoginCustomer } from '@/login/utils/types';
import { XmServerProperty } from '@/api-client/common/model';
import { capitalize, isEmpty, isNil } from 'lodash-es';
import { defineStore } from 'pinia';
import { Component, computed, defineAsyncComponent, watch } from 'vue';
import { useGetV7Property } from '@/api-client/common/queries/mode-controller/mode-controller';
import { CustomColumn } from '@/common/utils';

export type PropertyType = XmServerProperty & { useESLog?: boolean };
type LoginComponentMap = Record<LoginCustomer, Component>;

export interface CustomOption {
  useLoginAnimation?: boolean;
  useLoadingAnimation?: boolean;
  useLoginTitle?: boolean;
}

export interface FillerData {
  fieldName: string;
  displayName: string;
  visible: boolean;
}

const loginComponent: LoginComponentMap = {
  EXEMONE: defineAsyncComponent(() => import('@/login/components/loginForm/LoginForm.vue')),
  HYUNDAICAPITAL: defineAsyncComponent(
    () => import('@/login/components/loginForm/hyundaicapital/HyundaiCapitalLoginForm.vue'),
  ),
  HYUNDAICARD: defineAsyncComponent(
    () => import('@/login/components/loginForm/hyundaicard/HyundaiCardLoginForm.vue'),
  ),
  // KAKAOBANK: 전용 화면 없음 → EXEMONE 폼 사용
  KAKAOBANK: defineAsyncComponent(() => import('@/login/components/loginForm/LoginForm.vue')),
  LGE: defineAsyncComponent(() => import('@/login/components/loginForm/LoginForm.vue')),
  MONIMO: defineAsyncComponent(() => import('@/login/components/loginForm/LoginForm.vue')),
  NONGHYUP: defineAsyncComponent(() => import('@/login/components/loginForm/LoginForm.vue')),
  SHINHANBANK: defineAsyncComponent(
    () => import('@/login/components/loginForm/shinhan/ShinhanLoginForm.vue'),
  ),
};

const loadStyle: Record<LoginCustomer, null | (() => Promise<typeof import('*.scss')>)> = {
  [LOGIN_CUSTOMER.EXEMONE]: null,
  [LOGIN_CUSTOMER.HYUNDAICAPITAL]: () => import('@/common/styles/customer/_hyundaicapital.scss'),
  [LOGIN_CUSTOMER.HYUNDAICARD]: null,
  [LOGIN_CUSTOMER.KAKAOBANK]: null,
  [LOGIN_CUSTOMER.LGE]: () => import('@/common/styles/customer/_lge.scss'),
  [LOGIN_CUSTOMER.MONIMO]: () => import('@/common/styles/customer/_monimo.scss'),
  [LOGIN_CUSTOMER.NONGHYUP]: null,
  [LOGIN_CUSTOMER.SHINHANBANK]: null,
};

/**
 *   propertyMap 데이터를 가져와 Map외부의 동일한 키값의 값이 있으면 해당 값으로 덮어씌우고 없으면 propertyMap 내부의 값 그대로 사용
 *   propertyMap value값은 Map외부의 동일한 키에 값이 없으면 무조건 문자열로 들어옴 ex) "true"
 * */
const setDynamicPropertyObj = (propertyData: PropertyType): PropertyType['propertyMap'] => {
  const { propertyMap } = propertyData;
  if (!propertyMap) return propertyMap;

  // mutation 없이 새 객체 반환
  return Object.entries(propertyData).reduce(
    (acc, [key, value]) => {
      if (acc[key] && key !== 'propertyMap') {
        acc[key] = !isNil(value) ? value : acc[key];
      }
      return acc;
    },
    { ...propertyMap },
  );
};

export const usePropertyStore = defineStore('property', () => {
  // TODO: openAPI property 타입 editableUserInfo 추가 후 삭제
  // loginCustomer 오버라이드: 생성 타입 XmServerProperty는 SHINHANBANK 미포함 → 프런트 LoginCustomer로 확장.
  // TODO: 백엔드 OpenAPI에 SHINHANBANK 반영 후 loginCustomer 오버라이드 제거.
  type PropertyDataType = PropertyType & {
    editableUserInfo?: boolean;
    loginCustomer?: LoginCustomer;
  };

  /**
   * Vue Query: Property 데이터 (prefetch.ts에서 staleTime, gcTime: Infinity 설정됨)
   */
  const { data: queryData } = useGetV7Property<PropertyDataType>({
    query: {
      select: (response): PropertyDataType => {
        const data = response?.data?.data?.[0];
        if (data?.loginCustomer) {
          return data;
        }
        // localStorage fallback (삼성카드/모니모 환경)
        return JSON.parse(localStorage.getItem('initProperty') || '{}');
      },
    },
  });

  watch(queryData, (newData) => {
    if (newData?.loginCustomer) {
      // localStorage에 저장 (fallback용)
      webStorageController.setItem({
        type: 'local',
        key: 'initProperty',
        value: JSON.stringify(newData),
      });
    }
  });

  // undefined 방지용 computed wrapper
  const propertyData = computed<PropertyDataType>(
    () => queryData.value ?? ({} as PropertyDataType),
  );

  const isSetProperty = computed(() => !isEmpty(propertyData.value));
  const isSsoMode = computed(() => !!propertyData.value?.sso?.ssoMode);
  const isEsLogEnabled = computed(() => propertyData.value?.propertyMap?.useESLog === 'true');
  const isUseRememberId = computed(
    () => (propertyData.value?.propertyMap?.useRememberId ?? 'true') === 'true',
  );

  const isSessionTimerActive = () => !!propertyData.value.sessionTimeoutMode;
  const isRestrictDuplicateUsers = () => !!propertyData.value.restrictDuplicateUsers;
  const isValidUser = () =>
    Object.keys(LOGIN_CUSTOMER).includes(
      propertyData.value.loginCustomer ?? LOGIN_CUSTOMER.EXEMONE,
    );

  const propertyMapObj = computed(() => setDynamicPropertyObj(propertyData.value));

  const loginCustomer = computed(
    () =>
      (isValidUser() ? propertyData.value?.loginCustomer : LOGIN_CUSTOMER.EXEMONE) ||
      LOGIN_CUSTOMER.EXEMONE,
  );

  const loginOptions = computed<Record<LoginCustomer, CustomOption>>(() => {
    return {
      [LOGIN_CUSTOMER.EXEMONE]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: true,
      },
      [LOGIN_CUSTOMER.HYUNDAICAPITAL]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: false,
      },
      [LOGIN_CUSTOMER.HYUNDAICARD]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: true,
      },
      [LOGIN_CUSTOMER.KAKAOBANK]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: true,
      },
      [LOGIN_CUSTOMER.LGE]: {
        useLoginAnimation: false,
        useLoadingAnimation: false,
        useLoginTitle: true,
      },
      [LOGIN_CUSTOMER.MONIMO]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: true,
      },
      [LOGIN_CUSTOMER.NONGHYUP]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: true,
      },
      [LOGIN_CUSTOMER.SHINHANBANK]: {
        useLoginAnimation: propertyData.value.loginAnimeEnable,
        useLoadingAnimation: propertyData.value.loginAnimeEnable,
        useLoginTitle: true,
      },
    };
  });

  const getOptions = {
    loginOptions: () => loginOptions.value[loginCustomer.value],
    loginComponent: (customer?: LoginCustomer) => loginComponent[customer ?? loginCustomer.value],
    loadStyle: () => loadStyle[loginCustomer.value]?.(),
  };

  const isMatchedCustomer = (customer: LoginCustomer) => loginCustomer.value === customer;

  // filler - 트랜잭션 거래 추적
  const getFiller = (): FillerData[] => {
    return (propertyData.value?.fields as FillerData[])?.filter((field) => field.visible) ?? [];
  };

  const getFillerColumns = (hiddenDisplay = false, clickable = true): CustomColumn[] => {
    const fields = (propertyData.value?.fields as FillerData[]) ?? [];
    return fields.reduce((acc, { fieldName, displayName, visible }) => {
      if (visible) {
        acc.push({
          caption: capitalize(displayName),
          field: fieldName,
          type: 'string',
          hide: !visible,
          searchable: visible,
          sortable: visible,
          hiddenDisplay,
          clickable,
        });
      }
      return acc;
    }, [] as CustomColumn[]);
  };

  return {
    propertyData,
    isSetProperty,
    isSsoMode,
    isUseRememberId,
    isSessionTimerActive,
    loginCustomer,
    getOptions,
    isMatchedCustomer,
    isRestrictDuplicateUsers,
    isEsLogEnabled,
    propertyMapObj,
    getFiller,
    getFillerColumns,
  };
});
